import { z } from "zod";
import { approvalStatusSchema, ruleOriginSchema } from "../enums.js";
import { channelSchema } from "./channel.js";
import { validateTargeting } from "./common.js";
import { materialCategorySchema } from "./materialCategory.js";
import { policyLifecycleStatusSchema } from "./policy.js";
import { ruleChannelInputSchema } from "./rule.js";

export const applicabilityItemTypeSchema = z.enum(["POLICY", "RULE"]);
export type ApplicabilityItemType = z.infer<typeof applicabilityItemTypeSchema>;

export const applicabilityTargetCellSchema = z.object({
  channelId: z.string().min(1),
  categoryId: z.string().min(1),
  note: z.string().trim().max(500).nullable().optional(),
});
export type ApplicabilityTargetCell = z.infer<typeof applicabilityTargetCellSchema>;

export const applicabilityItemSchema = z.object({
  itemType: applicabilityItemTypeSchema,
  // Only meaningful when itemType is RULE — distinguishes manually authored
  // Custom Rules from AI-drafted Educate Sage rules.
  origin: ruleOriginSchema.optional(),
  itemId: z.string(),
  versionId: z.string(),
  version: z.number().int().positive(),
  title: z.string(),
  approvalStatus: approvalStatusSchema,
  editable: z.boolean(),
  // Rules have no archive/delete lifecycle, so they are always ACTIVE here.
  lifecycleStatus: policyLifecycleStatusSchema,
  appliesToAllChannels: z.boolean(),
  appliesToAllCategories: z.boolean(),
  channelNotesByChannelId: z.record(z.string().nullable()),
  categoryIds: z.array(z.string()),
  targetCells: z.array(applicabilityTargetCellSchema),
});
export type ApplicabilityItem = z.infer<typeof applicabilityItemSchema>;

export const applicabilityWorkspaceSchema = z.object({
  channels: z.array(channelSchema),
  categories: z.array(materialCategorySchema),
  items: z.array(applicabilityItemSchema),
});
export type ApplicabilityWorkspace = z.infer<typeof applicabilityWorkspaceSchema>;

export const updateApplicabilitySchema = z
  .object({
    appliesToAllChannels: z.boolean(),
    appliesToAllCategories: z.boolean(),
    channels: z.array(ruleChannelInputSchema).max(100).default([]),
    categoryIds: z.array(z.string().min(1)).max(100).default([]),
    targetCells: z.array(applicabilityTargetCellSchema).max(2_000).optional(),
  })
  .superRefine(validateTargeting);
export type UpdateApplicabilityInput = z.infer<typeof updateApplicabilitySchema>;
